import { OrbitControls } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { isPinned, type Piece, type Team } from '@halvard/rules';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Color, Vector3, type Group } from 'three';
import { columnHeight, PLINTH_HEIGHT, squareCenter, squareTop, TILE_SIZE } from './geometry.js';
import { COLORS, TEAM_COLORS, terrainColor } from './palette.js';
import { PieceModel } from './PieceModel.js';
import type { BoardHint, CameraView, Square, SummitMark } from './types.js';

export interface SceneProps {
  heights: number[][];
  pieces: readonly Piece[];
  ply: number;
  hints: readonly BoardHint[];
  visible: boolean[][] | null;
  summits: readonly SummitMark[];
  selected: Square | null;
  cursor: Square | null;
  lastMove: readonly Square[];
  view: CameraView;
  facing: Team;
  reducedMotion: boolean;
  onSquareClick: (r: number, c: number) => void;
}

export const BOARD_TARGET = new Vector3(0, 0.6, 0);

/** The parts of drei's OrbitControls the camera rig uses. */
interface Controls {
  target: Vector3;
  update: () => void;
  dampingFactor: number;
  enablePan: boolean;
  minDistance: number;
  maxDistance: number;
  maxPolarAngle: number;
}

/**
 * Camera position for a preset. `aspect` is the canvas width / height: narrow screens move the
 * camera back so the whole board stays in view.
 */
export function cameraPosition(view: CameraView, facing: Team, aspect = 1.4): Vector3 {
  const distance = Math.min(2.2, Math.max(1, Math.sqrt(1.35 / aspect)));
  switch (view) {
    case 'ivory':
      return new Vector3(0, 10.5, 11).multiplyScalar(distance);
    case 'basalt':
      return new Vector3(0, 10.5, -11).multiplyScalar(distance);
    case 'top':
      return new Vector3(0, 16 * distance, facing === 'I' ? 0.01 : -0.01);
  }
}

function heightOf(heights: number[][], r: number, c: number): number {
  return heights[r]?.[c] ?? 0;
}

/** Colour of a square out of sight: darker and greyer than the terrain colour. */
function fogged(color: string): string {
  return '#' + new Color(color).lerp(new Color(COLORS.fog), 0.6).getHexString();
}

export function Scene(props: SceneProps) {
  const { heights, pieces, ply, hints, view, facing, reducedMotion, onSquareClick } = props;

  const tall = heights.some((row) => row.some((h) => h > 4));

  const click = (r: number, c: number) => (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSquareClick(r, c);
  };

  return (
    <>
      <hemisphereLight args={['#DCE8F0', '#2E3B30', 1.35]} />
      <directionalLight
        position={[5, 12, 6]}
        color="#FFF2E2"
        intensity={2.3}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-7}
        shadow-camera-right={7}
        shadow-camera-top={7}
        shadow-camera-bottom={-7}
        shadow-bias={-0.0005}
      />
      <directionalLight position={[-6, 6, -5]} color="#AFCBFF" intensity={0.6} />

      <mesh position={[0, -PLINTH_HEIGHT / 2, 0]} receiveShadow>
        <boxGeometry args={[9.7, PLINTH_HEIGHT, 9.7]} />
        <meshStandardMaterial color={COLORS.plinth} roughness={0.95} />
      </mesh>

      {heights.map((row, r) =>
        row.map((h, c) => {
          const [x, z] = squareCenter(r, c);
          const height = columnHeight(h);
          return (
            <mesh
              key={`${r}:${c}`}
              position={[x, height / 2, z]}
              castShadow
              receiveShadow
              onClick={click(r, c)}
            >
              <boxGeometry args={[TILE_SIZE, height, TILE_SIZE]} />
              <meshStandardMaterial
                color={
                  props.visible && props.visible[r]?.[c] !== true
                    ? fogged(terrainColor(h, tall))
                    : terrainColor(h, tall)
                }
                roughness={1}
              />
            </mesh>
          );
        }),
      )}

      {props.summits.map((s) => {
        const [x, y, z] = squareTop(s.r, s.c, heightOf(heights, s.r, s.c));
        // A camp's own summit wears that camp's ring colour; the shared summit is trail orange.
        const color = s.team ? TEAM_COLORS[s.team].ring : COLORS.summit;
        return (
          <mesh
            key={`summit:${s.r}:${s.c}`}
            position={[x, y + 0.02, z]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <torusGeometry args={[0.42, 0.03, 10, 48]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} />
          </mesh>
        );
      })}

      {props.lastMove.map((s) => (
        <Frame
          key={`last:${s.r}:${s.c}`}
          square={s}
          heights={heights}
          color={COLORS.lastMove}
          width={0.04}
          opacity={0.55}
        />
      ))}
      {props.selected && (
        <Frame square={props.selected} heights={heights} color={COLORS.selected} width={0.07} />
      )}
      {props.cursor && (
        <Frame
          square={props.cursor}
          heights={heights}
          color={COLORS.cursor}
          width={0.05}
          lift={0.03}
        />
      )}

      {pieces
        .filter((p) => p.alive)
        .map((p) => (
          <AnimatedPiece
            key={p.id}
            target={squareTop(p.r, p.c, heightOf(heights, p.r, p.c))}
            raised={props.selected?.r === p.r && props.selected.c === p.c}
            reducedMotion={reducedMotion}
            onClick={click(p.r, p.c)}
          >
            <PieceModel team={p.team} type={p.type} pinned={isPinned({ ply }, p)} />
          </AnimatedPiece>
        ))}

      {hints.map((hint) => (
        <Hint
          key={`${hint.kind}:${hint.r}:${hint.c}`}
          hint={hint}
          height={heightOf(heights, hint.r, hint.c)}
        />
      ))}

      <OrbitControls makeDefault enableDamping target={BOARD_TARGET} />
      <CameraRig view={view} facing={facing} reducedMotion={reducedMotion} />
    </>
  );
}

/** Moves the camera to a preset whenever the preset changes, then leaves it to the user. */
export function CameraRig({
  view,
  facing,
  reducedMotion,
}: {
  view: CameraView;
  facing: Team;
  reducedMotion: boolean;
}) {
  const camera = useThree((s) => s.camera);
  const aspect = useThree((s) => s.size.width / Math.max(1, s.size.height));
  const controls = useThree((s) => s.controls) as unknown as Controls | null;
  const goal = useMemo(() => cameraPosition(view, facing, aspect), [view, facing, aspect]);
  const animating = useRef(true);

  useEffect(() => {
    if (!controls) return;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 6;
    controls.maxDistance = 40;
    controls.maxPolarAngle = Math.PI * 0.47;
  }, [controls]);

  useEffect(() => {
    animating.current = true;
    if (reducedMotion) {
      camera.position.copy(goal);
      controls?.target.copy(BOARD_TARGET);
      controls?.update();
      animating.current = false;
    }
  }, [goal, reducedMotion, camera, controls]);

  useFrame((_, delta) => {
    if (!animating.current) return;
    const t = 1 - Math.exp(-delta * 5);
    camera.position.lerp(goal, t);
    controls?.target.lerp(BOARD_TARGET, t);
    controls?.update();
    if (camera.position.distanceTo(goal) < 0.02) animating.current = false;
  });
  return null;
}

function AnimatedPiece(props: {
  target: [number, number, number];
  raised: boolean;
  reducedMotion: boolean;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  children: ReactNode;
}) {
  const ref = useRef<Group>(null);
  const [x, y, z] = props.target;
  const lift = props.raised ? 0.12 : 0;

  useFrame((_, delta) => {
    const group = ref.current;
    if (!group) return;
    if (props.reducedMotion) {
      group.position.set(x, y + lift, z);
      return;
    }
    const t = 1 - Math.exp(-delta * 12);
    group.position.x += (x - group.position.x) * t;
    group.position.z += (z - group.position.z) * t;
    group.position.y += (y + lift - group.position.y) * t;
  });

  return (
    <group ref={ref} position={[x, y, z]} onClick={props.onClick}>
      {props.children}
    </group>
  );
}

/** A square outline on top of a tile. */
function Frame(props: {
  square: Square;
  heights: number[][];
  color: string;
  width: number;
  lift?: number;
  opacity?: number;
}) {
  const { r, c } = props.square;
  const [x, y, z] = squareTop(r, c, heightOf(props.heights, r, c));
  const half = TILE_SIZE / 2 - props.width / 2;
  const edges: [number, number, number, number][] = [
    [0, -half, TILE_SIZE, props.width],
    [0, half, TILE_SIZE, props.width],
    [-half, 0, props.width, TILE_SIZE],
    [half, 0, props.width, TILE_SIZE],
  ];
  return (
    <group position={[x, y + 0.012 + (props.lift ?? 0), z]}>
      {edges.map(([dx, dz, w, d]) => (
        <mesh key={`${dx}:${dz}`} position={[dx, 0, dz]}>
          <boxGeometry args={[w, 0.02, d]} />
          <meshBasicMaterial
            color={props.color}
            transparent={props.opacity !== undefined}
            opacity={props.opacity ?? 1}
          />
        </mesh>
      ))}
    </group>
  );
}

/** Move: pale disc. Capture: orange ring. Pin: yellow ring. Raise / lower: cyan arrow. */
function Hint({ hint, height }: { hint: BoardHint; height: number }) {
  const [x, y, z] = squareTop(hint.r, hint.c, height);
  switch (hint.kind) {
    case 'move':
      return (
        <mesh position={[x, y + 0.02, z]}>
          <cylinderGeometry args={[0.16, 0.16, 0.03, 32]} />
          <meshBasicMaterial color={COLORS.moveHint} transparent opacity={0.85} />
        </mesh>
      );
    case 'capture':
    case 'pin':
      return (
        <mesh position={[x, y + 0.06, z]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[hint.kind === 'capture' ? 0.4 : 0.43, 0.05, 10, 40]} />
          <meshBasicMaterial
            color={hint.kind === 'capture' ? COLORS.captureHint : COLORS.pinHint}
          />
        </mesh>
      );
    case 'raise':
    case 'lower':
      return (
        <group position={[x, y + 0.32, z]} rotation={[hint.kind === 'raise' ? 0 : Math.PI, 0, 0]}>
          <mesh position={[0, -0.08, 0]}>
            <cylinderGeometry args={[0.05, 0.05, 0.26, 12]} />
            <meshBasicMaterial color={COLORS.buildHint} />
          </mesh>
          <mesh position={[0, 0.12, 0]}>
            <coneGeometry args={[0.14, 0.18, 16]} />
            <meshBasicMaterial color={COLORS.buildHint} />
          </mesh>
        </group>
      );
  }
}
